<template>
  <div class="otp">
    <input
      v-for="(digit, index) in digits"
      :key="index"
      :ref="(el) => (boxes[index] = el)"
      v-model="digits[index]"
      class="otp__box"
      type="tel"
      inputmode="numeric"
      maxlength="1"
      autocomplete="one-time-code"
      :aria-label="`${index + 1}/${length}`"
      @input="onInput(index, $event)"
      @keydown.delete="onDelete(index, $event)"
      @paste="onPaste"
    />
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue'

const props = defineProps({
  length: { type: Number, default: 4 },
  modelValue: { type: String, default: '' },
  autofocus: { type: Boolean, default: true }
})
const emit = defineEmits(['update:modelValue', 'complete'])

const digits = reactive(Array.from({ length: props.length }, () => ''))
const boxes = ref([])

function emitValue() {
  const value = digits.join('')
  emit('update:modelValue', value)
  if (value.length === props.length && !digits.includes('')) emit('complete', value)
}

// A browser autofill burst can drop the whole code into one box: spread it across the boxes.
function onInput(index, event) {
  const typed = event.target.value.replace(/\D/g, '')
  if (typed.length > 1) {
    fill(typed, index)
    return
  }
  digits[index] = typed.slice(-1)
  if (typed && index < props.length - 1) boxes.value[index + 1]?.focus()
  emitValue()
}

function fill(code, from = 0) {
  for (let i = 0; i < props.length; i++) digits[i] = code[i - from] ?? (i < from ? digits[i] : '')
  boxes.value[Math.min(from + code.length, props.length - 1)]?.focus()
  emitValue()
}

function onDelete(index, event) {
  if (!digits[index] && index > 0) {
    boxes.value[index - 1]?.focus()
    digits[index - 1] = ''
    event.preventDefault()
    emitValue()
  }
}

function onPaste(event) {
  event.preventDefault()
  fill((event.clipboardData?.getData('text') || '').replace(/\D/g, ''), 0)
}

watch(
  () => props.modelValue,
  (value) => {
    if (value === digits.join('')) return
    for (let i = 0; i < props.length; i++) digits[i] = value[i] || ''
  }
)

onMounted(() => {
  if (props.autofocus) boxes.value[0]?.focus()
})
</script>

<style scoped>
.otp {
  display: flex;
  justify-content: center;
  gap: var(--spacing-md);
}

.otp__box {
  width: 60px;
  height: 64px;
  background-color: var(--color-surface);
  border: 2px solid var(--color-surface-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  font-family: var(--font-display);
  font-size: 1.75rem;
  font-weight: 700;
  text-align: center;
  caret-color: var(--color-primary);
  transition: border-color var(--transition-fast);
}

.otp__box:focus {
  border-color: var(--color-primary);
}

@media (max-width: 360px) {
  .otp {
    gap: var(--spacing-sm);
  }

  .otp__box {
    width: 52px;
    height: 56px;
  }
}
</style>
